/*****************************************
 ./src/components/Modal/Modal.tsx
 *****************************************/

 import React, { ReactNode } from 'react';
 import { Modal as MuiModal, Box, Fade, Backdrop, IconButton } from '@mui/material';
 import CloseIcon from '@mui/icons-material/Close';
 import * as styles from './Modal.module.scss';
 
 interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm?: () => void;
    title?: string | ReactNode;
    message?: ReactNode;
    confirmText?: string | React.ReactNode;
    cancelText?: string;
    children?: ReactNode;
    startIcon?: ReactNode;
    buttonDisabled?: boolean;
    cancelButtonDisabled?: boolean; // Separate prop for cancel button - defaults to false so cancel is always available
    hasBorderBox?: boolean;
    showConfirmButton?: boolean;
    showCancelButton?: boolean;
    showCloseButton?: boolean;
    width?: string;
    minWidth?: string;
    className?: string;
    centered?: boolean;
   size?: 'auto' | 'small' | 'medium' | 'large' | 'full' | string; // 'auto' hugs content, others set max-width
    // Theme color props for guest page theming
    backgroundColor?: string;
    textColor?: string;
    // Custom content areas for special modals
    headerContent?: ReactNode; // Content above title (e.g., icons, images)
    headerContentBackground?: string; // Background image URL for headerContent area only
    backgroundImage?: string; // URL for background image (entire modal)
    customStyles?: React.CSSProperties; // Inline styles for modal box
}
 
 const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Bekræftelse",
    children,
    message,
    confirmText = "Bekræft",
    cancelText = "Fortryd",
    buttonDisabled = false,
    cancelButtonDisabled = false, // Cancel button is always enabled by default
    hasBorderBox = false,
    showConfirmButton = true,
    showCancelButton = true,
    showCloseButton = false,
    className = "",
    centered = false,
    size = "medium",
    backgroundColor = "white", // Default white background
    textColor = "inherit", // Default inherit text color
    headerContent,
    headerContentBackground,
    backgroundImage,
    customStyles = {}
}) => {
    if (!isOpen) return null;

    // Strip transparency from background color to ensure fully opaque modal background
    const opaqueBackgroundColor = backgroundColor.includes('rgba') 
        ? backgroundColor.replace(/,\s*[\d.]+\)$/, ', 1)')
        : backgroundColor.includes('rgb') 
            ? backgroundColor.replace('rgb', 'rgba').replace(')', ', 1)')
            : backgroundColor;

    return (
         <MuiModal
             open={isOpen}
             onClose={onClose}
             aria-labelledby="modal-title"
             aria-describedby="modal-description"
             closeAfterTransition
             slots={{
                 backdrop: Backdrop
             }}
             slotProps={{
                 backdrop: {
                     timeout: 300,
                     sx: {
                         backgroundColor: 'rgba(0, 0, 0, 0.6)',
                         backdropFilter: 'blur(4px)',
                     }
                 }
             }}
         >
             <Fade in={isOpen} timeout={{ enter: 400, exit: 200 }}>
                <Box
                    className={`${styles.modalContentWrapper} ${className} ${size ? `has-size-${size}` : ''} ${headerContent ? styles.hasHeaderContent : ''}`}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: centered ? 'center' : 'left',
                        outline: 'none',
                        // Set CSS custom properties for SCSS to use
                        '--modal-bg-color': opaqueBackgroundColor,
                        '--modal-text-color': textColor,
                        // Background image if provided
                        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        // Apply custom styles (but don't override width - let CSS handle it for transitions)
                        ...customStyles,
                    }}
                >
                     {showCloseButton && (
                                                 <IconButton
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                backgroundColor: 'transparent', // Remove white background
                                border: 'none', // Remove border
                                width: 32,
                                height: 32,
                                color: textColor, // Use theme text color for the icon
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)', // Subtle hover effect
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: 18,
                                    fontWeight: 'bold',
                                    // Make the X more square-like in its bounding box
                                    transform: 'scale(1.1)',
                                    strokeWidth: 0.5,
                                }
                            }}
                            aria-label="close"
                        >
                             <CloseIcon />
                         </IconButton>
                     )}
                     
                     {/* Header content area (icons, images, decorations) */}
                     {headerContent && (
                         <div 
                             className={styles.modalHeaderContent}
                             style={{
                                 backgroundImage: headerContentBackground ? `url(${headerContentBackground})` : 'none',
                                 backgroundSize: 'cover',
                                 backgroundPosition: 'center',
                                 backgroundRepeat: 'no-repeat',
                             }}
                         >
                             {headerContent}
                         </div>
                     )}
                     
                     {title && !hasBorderBox && <h2 id="modal-title" className={styles.modalTitle}>{title}</h2>}
 
                     <div className={styles.modalcontent}>
                         <div id="modal-description" className={`modal-body ${styles.modalBody} ${hasBorderBox ? 'border-box-center-title small-box' : ''}`}>
                             {title && hasBorderBox && <h6 className="section-title">{title}</h6>}
                             {children || message}
                         </div>
                         {(showConfirmButton || showCancelButton) && (
                             <div
                                 className={styles.modalActions}
                                 style={{ justifyContent: centered ? 'center' : 'flex-start' }}
                             >
                                 {showConfirmButton && (
                                     <button
                                         className="invidoo-button primary modal-confirm-button small"
                                         role="button"
                                         onClick={onConfirm}
                                         disabled={buttonDisabled}
                                     >
                                         <span>{confirmText}</span>
                                     </button>
                                 )}
                                 {showCancelButton && (
                                     <button
                                         className="invidoo-button secondary modal-cancel-button small"
                                         role="button"
                                         onClick={onClose}
                                         disabled={cancelButtonDisabled}
                                     >
                                         <span>{cancelText}</span>
                                     </button>
                                 )}
                             </div>
                         )}
                     </div>
                 </Box>
             </Fade>
         </MuiModal>
     );
 };
 
 export default Modal;